import { InstitutionIntegrationType } from '@breakoutlearning/firebase-repository/types'

type LmsBranding = {
  /**
   * Path under public/. The artwork is white-on-transparent, so it only reads
   * on a dark backing — see LmsMark.
   */
  src: string
  /**
   * How the vendor is named in the product. Not translated — these are brand
   * names. `Section.lmsName` in firebase-repository still spells the same two
   * out for its own consumers and can't import this, so change both together
   * until those consumers move over here.
   */
  label: string
}

/**
 * The single place an LMS is mapped to its artwork and name.
 *
 * Adding a member to `InstitutionIntegrationType` is a type error until it is
 * registered here, so a new integration can't silently render nothing. To add
 * one: drop `source-<value>.png` into `public/assets/images/lti/` — where
 * `<value>` is the enum's *stored* value — and add a line below.
 */
const LMS_BRANDING: Record<InstitutionIntegrationType, LmsBranding> = {
  [InstitutionIntegrationType.canvas]: {
    src: '/assets/images/lti/source-canvas.png',
    label: 'Canvas',
  },
  // Enum key is `d2l`, stored value is 'brightspace' (kept for back-compat),
  // which is what the filename follows.
  [InstitutionIntegrationType.d2l]: {
    src: '/assets/images/lti/source-brightspace.png',
    label: 'D2L',
  },
  // A generic LTI 1.3 platform has no vendor to brand, so it gets a neutral
  // mark rather than being left blank.
  [InstitutionIntegrationType.generic]: {
    src: '/assets/images/lti/source-generic.png',
    label: 'Generic',
  },
}

export function lmsBrandingFor(
  lmsKey: InstitutionIntegrationType | null | undefined
): LmsBranding | null {
  return lmsKey ? LMS_BRANDING[lmsKey] : null
}

/**
 * Narrow an untyped stored value (e.g. `UserProfile.ltiSource`, which is a bare
 * string) to a key the registry can be indexed with.
 */
export function toLmsKey(
  value: string | null | undefined
): InstitutionIntegrationType | null {
  if (!value) return null
  return Object.values(InstitutionIntegrationType).includes(
    value as InstitutionIntegrationType
  )
    ? (value as InstitutionIntegrationType)
    : null
}
